import React, { useEffect, useRef } from 'react';
import { useChatStore } from '@core/hooks/use-chat-store';
import { Summary, Message } from '../../../../types';
import { getApiUrl } from '../../../../lib/hooks/use-new-api';
import { getCTADialogType } from '../../../../lib/error-utils';
import { ChatInput } from './chat/ChatInput';
import { Chat } from './chat/Chat';
import { SuggestedQuestions } from './chat/SuggestedQuestions';
import { useChat } from '../../../../lib/use-chat';
import { useSummarizer } from '@core/store/useSummarizer';

interface ChatTabProps {
  summary: Summary;
}

export const ChatTab: React.FC<ChatTabProps> = ({ summary }) => {
  const { messages: storedMessages, addMessage } = useChatStore();
  const { quota, triggerCTADialog } = useSummarizer();

  // Determine which API to use
  const apiUrl = getApiUrl('/ai-chat');

  // Format body based on API type
  const requestBody = {
    summary_id: summary.id,
    transcript: summary.transcript,
    user_id: quota?.user_id,
  };

  const {
    messages,
    setMessages,
    input,
    handleInputChange,
    handleSubmit,
    append,
    isLoading,
    isStreaming: isStreaming,
  } = useChat({
    api: apiUrl,
    initialMessages: storedMessages[summary.id] || [],
    body: requestBody,
    onError: (err) => {
      // Check if we should show CTA modal
      if (err.shouldShowCTA) {
        const ctaType = getCTADialogType(err);
        if (ctaType) {
          triggerCTADialog(ctaType, 'chat', err.message);
        }
      }

      // Add error message to chat as system message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        content:
          err.code === 'QUOTA_EXCEEDED'
            ? 'You have reached your daily limit. Please upgrade to continue chatting.'
            : `Sorry, I encountered an error: ${err.message}`,
        role: 'assistant',
      };

      setMessages((messages) => {
        return messages.concat(errorMessage);
      });
    },
  });

  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Sync stored messages only on initial load
  useEffect(() => {
    const currentMessages = storedMessages[summary.id];
    if (currentMessages && currentMessages.length > 0 && messages.length === 0) {
      setMessages(currentMessages);
    }
  }, [summary.id]); // Only run when summary changes, not on every message update

  const handleQuestionSelect = (question: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content: question,
      role: 'user',
    };
    append(userMessage);
  };

  const showWelcomeMessage = messages.length === 0;

  return (
    <div className="flex h-full flex-col p-4">
      <div
        ref={chatContainerRef}
        className="flex-1 space-y-4 overflow-y-auto pr-2 min-h-[200px]"
        style={{ maxHeight: 'calc(100vh - 250px)' }}
      >
        {showWelcomeMessage && (
          <div className="text-center">
            <p className="text-lg font-medium">AI Chat</p>
            <p className="text-sm text-gray-500">
              Hi there! How can I assist you today? Feel free to ask me anything about the video.
            </p>
          </div>
        )}
        <Chat messages={messages} loading={isLoading} isStreaming={isStreaming} />
      </div>

      <div className="mt-auto pt-4 sticky bottom-0 w-full">
        {showWelcomeMessage && <SuggestedQuestions onQuestionSelect={handleQuestionSelect} />}
        <div className="mt-4">
          <ChatInput
            input={input}
            handleInputChange={handleInputChange}
            handleSubmit={(e) => {
              const userMessage: Message = {
                id: Date.now().toString(),
                content: input,
                role: 'user',
              };
              addMessage(summary.id, userMessage);
              handleSubmit(e, { input });
            }}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
